import type { TextFieldProps, TextFieldVariants } from "@mui/material";
import { TextField as MaterialTextInput } from "@mui/material";
import { useField } from "formik";
import type { ComponentProps, Ref } from "react";

import { useFieldContext } from "../FormFieldContext";
import type { TestAttribute } from "../shared";
import { useFieldLabelAndDescription, useFieldPlaceholder, useHandleBlur } from "./helpers";

type TextProps = Omit<TextFieldProps, "value" | "css"> &
  TestAttribute & {
    ref?: Ref<HTMLInputElement>;
    value?: string;
    maxLength?: number;
  };

export const Text = ({ value, size, variant, dataTest, slotProps, maxLength, ref, ...rest }: TextProps) => {
  return (
    <MaterialTextInput
      {...rest}
      ref={ref}
      value={value ?? ""}
      variant={variant ?? "standard"}
      size={size ?? "small"}
      slotProps={{
        ...slotProps,
        htmlInput: {
          ...slotProps?.htmlInput,
          maxLength: maxLength,
          "data-test": `${dataTest ?? rest.name}-input`,
        },
      }}
    />
  );
};

export type FieldTextProps = Omit<TextProps, "id" | "name" | "value" | "onBlur" | "onKeyPress"> & {
  hideLabel?: boolean;
  hideHelperText?: boolean;
};

export const FieldText = ({
  hideLabel,
  hideHelperText,
  onChange,
  label,
  helperText,
  placeholder,
  variant,
  ref,
  ...rest
}: FieldTextProps) => {
  const { controlId, dataTest, name } = useFieldContext();

  const fieldPlaceholder = useFieldPlaceholder(name);
  const [fieldLabel, fieldHelperText] = useFieldLabelAndDescription(name);

  const [{ value, onChange: formikOnChange }, { error, touched }] = useField<string>(name);

  const handleBlur = useHandleBlur();

  const handleChange: ComponentProps<typeof Text>["onChange"] = (e) => {
    formikOnChange(e);
    onChange?.(e);
  };

  return (
    <Text
      {...rest}
      id={controlId}
      ref={ref}
      name={name}
      error={touched && Boolean(error)}
      variant={variant as TextFieldVariants}
      placeholder={placeholder ?? fieldPlaceholder}
      value={value}
      label={hideLabel ? "" : (label ?? fieldLabel)}
      helperText={hideHelperText ? "" : (helperText ?? fieldHelperText)}
      onBlur={handleBlur}
      onChange={handleChange}
      dataTest={dataTest}
    />
  );
};
